import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Collapse, CircularProgress, Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ExpandMore from '@material-ui/icons/ExpandMore'
import ChevronRight from '@material-ui/icons/ChevronRight'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'

const useStyles = makeStyles(() => ({
  root: {
    padding: '16px'
  },
  section: {
    marginBottom: '12px',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '6px',
    overflow: 'hidden'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
    userSelect: 'none',
    fontWeight: 600,
    fontSize: 'var(--font-size-sm)',
    background: 'var(--color-neutral-50)',
    borderBottom: '1px solid var(--color-neutral-200)',
    '&:hover': {
      background: 'var(--color-neutral-100)'
    }
  },
  sectionBody: {
    padding: '8px 0'
  },
  fieldRow: {
    display: 'flex',
    padding: '3px 12px',
    fontSize: 'var(--font-size-xs)',
    lineHeight: 1.6,
    '&:hover': {
      background: 'var(--color-neutral-50)'
    }
  },
  label: {
    width: '45%',
    flexShrink: 0,
    color: 'var(--text-color-subtle)',
    fontWeight: 500
  },
  value: {
    width: '55%',
    wordBreak: 'break-word'
  },
  notesRow: {
    display: 'flex',
    flexDirection: 'column',
    padding: '3px 12px',
    fontSize: 'var(--font-size-xs)',
    lineHeight: 1.6
  },
  notesValue: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    background: 'var(--color-neutral-50)',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '4px',
    padding: '8px 10px',
    fontSize: 'var(--font-size-xs)',
    lineHeight: 1.5,
    maxHeight: '200px',
    overflow: 'auto',
    marginTop: '2px'
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 0'
  },
  empty: {
    padding: '24px',
    textAlign: 'center',
    color: 'var(--text-color-subtle)',
    fontSize: 'var(--font-size-sm)'
  },
  jsonValue: {
    fontFamily: 'monospace',
    fontSize: '11px',
    whiteSpace: 'pre-wrap',
    maxHeight: '200px',
    overflow: 'auto',
    background: 'var(--color-neutral-50)',
    padding: '4px 6px',
    borderRadius: '4px'
  }
}))

function resolveValue (data, path) {
  if (!data || !path) return undefined
  const parts = path.split('.')
  let current = data
  for (const part of parts) {
    if (current == null) return undefined
    current = current[part]
  }
  return current
}

const MEASURE_FORMAT_MAP = {
  cash: '$(0,0.00)',
  units: '(0,0.00)',
  date: 'date'
}

function resolveMeasureFormat (measure) {
  if (!measure) return undefined
  return MEASURE_FORMAT_MAP[measure] ?? measure
}

function formatFieldValue (formatter, value, type, measure) {
  if (value == null || value === '') return '--'

  if (type === 'bool') {
    if (value === true || value === 1 || value === '1' || value === 'true') return 'Yes'
    if (value === false || value === 0 || value === '0' || value === 'false') return 'No'
    return String(value)
  }

  if (type === 'json') {
    return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)
  }

  if (type === 'notes') {
    return String(value)
  }

  const formatString = resolveMeasureFormat(measure)
  if (formatString && formatter) {
    try {
      return formatter(value, formatString)
    } catch {
      return String(value)
    }
  }

  if (type === 'date' && typeof value === 'string') {
    return value.slice(0, 10)
  }

  return String(value)
}

function FieldValue ({ value, type, measure, formatter, classes }) {
  const formatted = formatFieldValue(formatter, value, type, measure)
  if (type === 'json' && value != null && typeof value === 'object') {
    return <div className={classes.jsonValue}>{formatted}</div>
  }
  if (type === 'notes') {
    return <div className={classes.notesValue}>{formatted}</div>
  }
  return <span>{formatted}</span>
}

FieldValue.propTypes = {
  value: PropTypes.any,
  type: PropTypes.string,
  measure: PropTypes.string,
  formatter: PropTypes.func,
  classes: PropTypes.object
}

function Section ({ section, data, formatter, classes }) {
  const [open, setOpen] = useState(true)
  const toggle = useCallback(() => setOpen(prev => !prev), [])
  const fields = section.fields || []

  if (!fields.length) return null

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader} onClick={toggle} role='button' tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && toggle()}>
        {open ? <ExpandMore fontSize='small' /> : <ChevronRight fontSize='small' />}
        {section.title}
      </div>
      <Collapse in={open}>
        <div className={classes.sectionBody}>
          {fields.map(field => {
            const isNotes = field.type === 'notes'
            return (
              <div key={field.field} className={isNotes ? classes.notesRow : classes.fieldRow}>
                <div className={classes.label}>{field.label}</div>
                <div className={isNotes ? undefined : classes.value}>
                  <FieldValue
                    value={resolveValue(data, field.field)}
                    type={field.type}
                    measure={field.measure}
                    formatter={formatter}
                    classes={classes}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </Collapse>
    </div>
  )
}

Section.propTypes = {
  section: PropTypes.object.isRequired,
  data: PropTypes.object,
  formatter: PropTypes.func,
  classes: PropTypes.object.isRequired
}

export default function TransactionDetailView ({ data, displayMeta, isLoading, layout }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()
  const sectionMd = layout === 'narrow' ? 12 : 6

  if (isLoading) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={32} />
      </div>
    )
  }

  if (!data || !displayMeta) {
    return <div className={classes.empty}>No transaction data available.</div>
  }

  const sections = Array.isArray(displayMeta.sections) ? displayMeta.sections : []

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        {sections.map(section => (
          <Grid item xs={12} md={sectionMd} key={section.key}>
            <Section
              section={section}
              data={data}
              formatter={formatter}
              classes={classes}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  )
}

TransactionDetailView.propTypes = {
  data: PropTypes.object,
  displayMeta: PropTypes.shape({
    sections: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      fields: PropTypes.array.isRequired
    }))
  }),
  isLoading: PropTypes.bool,
  layout: PropTypes.oneOf(['wide', 'narrow'])
}
